import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, PlayCircle } from "lucide-react";

/**
 * CrewActionVideo — inline autoplay loop above the RealJobsGallery.
 * Cinematic full-width crew action clip with dark grade + HUD frame,
 * mute toggle, and CSS fallback if video fails.
 */
export default function CrewActionVideo() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [failed, setFailed] = useState(false);

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "var(--bg)", padding: "80px 0 40px" }}
      data-testid="crew-action-video"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-10"
        >
          <div className="md:col-span-3 overline pt-2">Crew · In motion</div>
          <div className="md:col-span-9">
            <h2 className="font-display" style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)", letterSpacing: "-0.03em", lineHeight: 1 }}>
              Fifty-four seconds of the real thing —{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>no b-roll, no actors.</span>
            </h2>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: "16 / 9",
            borderRadius: "28px",
            border: "1px solid var(--line-gold)",
            boxShadow: "0 50px 100px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.03) inset",
          }}
        >
          {!failed ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              src="/brand/video/bespoke-crew-action.mp4"
              poster="/brand/video/bespoke-crew-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onError={(e) => {
                // Only fall back on actual media errors, not on transient network hiccups
                const err = e.currentTarget?.error;
                if (err && err.code >= 3) setFailed(true);
              }}
              style={{ filter: "brightness(0.85) contrast(1.05) saturate(0.95)" }}
              data-testid="crew-video"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center flex-col gap-3" style={{
              background: "linear-gradient(160deg, #0a0a0d 0%, #14141a 60%, #0a0a0d 100%)",
            }}>
              <PlayCircle size={40} style={{ color: "var(--gold-2)" }} strokeWidth={1.2} />
              <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                Video unavailable · scroll for photos
              </div>
            </div>
          )}

          {/* Dark grade */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: "radial-gradient(1000px 500px at 50% 40%, transparent 40%, rgba(0,0,0,0.45) 100%)",
          }} />

          {/* HUD corner marks */}
          {["tl", "tr", "bl", "br"].map((c) => {
            const isTop = c.startsWith("t"), isLeft = c.endsWith("l");
            return (
              <div key={c} className="absolute pointer-events-none" style={{
                [isTop ? "top" : "bottom"]: "20px",
                [isLeft ? "left" : "right"]: "20px",
                width: "26px", height: "26px",
                borderTop: isTop ? "1px solid var(--gold)" : "none",
                borderBottom: !isTop ? "1px solid var(--gold)" : "none",
                borderLeft: isLeft ? "1px solid var(--gold)" : "none",
                borderRight: !isLeft ? "1px solid var(--gold)" : "none",
                opacity: 0.6,
              }} />
            );
          })}

          {/* Top-left live badge */}
          <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5" style={{
            background: "rgba(0,0,0,0.55)",
            border: "1px solid var(--line-gold)",
            borderRadius: "999px",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>
              Field footage · Real crew
            </span>
          </div>

          {/* Mute toggle */}
          <button
            onClick={toggleMute}
            className="absolute bottom-6 right-6 w-11 h-11 flex items-center justify-center transition-all hover:scale-105"
            style={{
              background: "rgba(0,0,0,0.65)",
              border: "1px solid var(--line-gold)",
              borderRadius: "999px",
              backdropFilter: "blur(10px)",
              color: "var(--gold-2)",
            }}
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid="crew-video-mute"
            data-cursor="hover"
            data-cursor-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX size={15} strokeWidth={1.5} /> : <Volume2 size={15} strokeWidth={1.5} />}
          </button>

          {/* Bottom-left runtime */}
          <div className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
            RUNTIME · 00:54
          </div>
        </motion.div>
      </div>
    </section>
  );
}
